import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import supabase from "../lib/supabaseClient";
import Button from '../components/ui/button';
import { 
  CreditCard, 
  Download, 
  Calendar,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Trash2,
  Plus,
  Zap,
  Crown,
  RotateCcw,
  AlertTriangle,
  ArrowRight,
  Lock,
  Shield,
  Users,
  UserPlus,
  MessageSquare,
  BarChart3,
  Check,
  X,
  ExternalLink,
  Phone
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
// Price comes from the same table the Stripe webhook and referral credits use, so
// the plan card can never drift from what the customer is actually charged. This
// page previously carried its own hardcoded prices and had gone stale on three of
// four tiers (Starter $97/Growth $497/Scale $1997), quoting under the real charge.
import { PLAN_PRICE } from '../lib/planPricing';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Full plan catalog shown in the "View Plans" modal.
// MIRRORS the public pricing page (rei-crm-marketing/src/app/pricing/PricingClient.tsx).
// Keep the two in sync: a customer comparing this modal against getsurfox.com/pricing
// must see the same volumes and the same feature list. Price is pulled from
// planPricing.js so neither copy can drift from what Stripe charges.
const PLAN_CATALOG = [
  {
    slug: 'starter',
    name: 'Starter',
    price: PLAN_PRICE.starter,
    volume: '2,000 messages / mo',
    desc: 'For operators testing the waters on a focused list.',
    callout: 'Core AI that texts, qualifies, and books. Conversation learning not included yet.',
    popular: false,
    groups: [
      {
        h: 'Monthly limits',
        items: [
          { t: '2,000 SMS messages per month', sub: 'Each conversation uses ~4 to 8 messages (your outbound plus lead replies combined).' },
          { t: '200 website chat conversations per month', sub: 'Qualifies inbound website visitors 24/7.' },
          { t: '1 team member account' }
        ]
      },
      {
        h: 'AI capabilities',
        items: [
          { t: 'AI conversation engine' },
          { t: '50 qualification signals tracked' },
          { t: 'Knowledge base upload' },
          { t: 'Google & Outlook calendar booking' },
          { t: 'Conversation learning', off: true }
        ]
      },
      {
        h: 'Support & compliance',
        items: [
          { t: 'Basic analytics dashboard' },
          { t: 'Email support' },
          { t: 'Automatic opt-out detection. STOP and inferred opt-out language honored instantly' },
          { t: 'Built on TCPA-compliant infrastructure' }
        ]
      }
    ]
  },
  {
    slug: 'growth',
    name: 'Growth',
    price: PLAN_PRICE.growth,
    volume: '10,000 messages / mo',
    desc: 'For teams ready to work a full list every month.',
    callout: 'Learning AI that studies your last 100 conversations and gets sharper over time.',
    popular: true,
    groups: [
      {
        h: 'Monthly limits',
        items: [
          { t: '10,000 SMS messages per month', sub: 'Each conversation uses ~4 to 8 messages (your outbound plus lead replies combined).' },
          { t: '500 website chat conversations per month', sub: 'Qualifies inbound website visitors 24/7.' },
          { t: '5 team member accounts' }
        ]
      },
      {
        h: 'AI capabilities',
        items: [
          { t: 'Everything in Starter, plus:' },
          { t: 'Conversation learning (last 100)' },
          { t: 'A/B testing & optimization' },
          { t: 'Zapier integration' },
          { t: 'Chrome extension for lead capture' }
        ]
      },
      {
        h: 'Support & compliance',
        items: [
          { t: 'Priority email & chat support' },
          { t: 'Automatic opt-out detection. STOP and inferred opt-out language honored instantly' },
          { t: 'Built on TCPA-compliant infrastructure' }
        ]
      }
    ]
  },
  {
    slug: 'growth_plus',
    name: 'Growth Plus',
    price: PLAN_PRICE.growth_plus,
    volume: '25,000 messages / mo',
    desc: 'Full Scale intelligence, sized for a 25,000 message month.',
    callout: 'Everything in Scale, including learning from your last 1,000 conversations. Just 25,000 messages.',
    popular: false,
    groups: [
      {
        h: 'Monthly limits',
        items: [
          { t: '25,000 SMS messages per month', sub: 'Each conversation uses ~4 to 8 messages (your outbound plus lead replies combined).' },
          { t: '2,000 website chat conversations per month', sub: 'Qualifies inbound website visitors 24/7.' },
          { t: '15 team member accounts' }
        ]
      },
      {
        h: 'AI capabilities',
        items: [
          { t: 'Everything in Growth, plus:' },
          { t: 'Conversation learning (last 1,000)' },
          { t: 'Multiple personas & campaigns' },
          { t: 'Unlimited knowledge base uploads' }
        ]
      },
      {
        h: 'Support & compliance',
        items: [
          { t: 'White-glove onboarding' },
          { t: 'Dedicated success manager' },
          { t: 'Automatic opt-out detection. STOP and inferred opt-out language honored instantly' },
          { t: 'Built on TCPA-compliant infrastructure' }
        ]
      }
    ]
  },
  {
    slug: 'scale',
    name: 'Scale',
    price: PLAN_PRICE.scale,
    volume: '40,000 messages / mo',
    desc: 'For high-volume operations running constant campaigns.',
    callout: 'Maximum intelligence. Learns from your last 1,000 conversations across every campaign.',
    popular: false,
    groups: [
      {
        h: 'Monthly limits',
        items: [
          { t: '40,000 SMS messages per month', sub: 'Each conversation uses ~4 to 8 messages (your outbound plus lead replies combined).' },
          { t: '5,000 website chat conversations per month', sub: 'Qualifies inbound website visitors 24/7.' },
          { t: '15+ team member accounts' }
        ]
      },
      {
        h: 'AI capabilities',
        items: [
          { t: 'Everything in Growth, plus:' },
          { t: 'Conversation learning (last 1,000)' },
          { t: 'Multiple personas & campaigns' },
          { t: 'Unlimited knowledge base uploads' }
        ]
      },
      {
        h: 'Support & compliance',
        items: [
          { t: 'White-glove onboarding' },
          { t: 'Dedicated success manager' },
          { t: 'Automatic opt-out detection. STOP and inferred opt-out language honored instantly' },
          { t: 'Built on TCPA-compliant infrastructure' }
        ]
      }
    ]
  }
];

export default function BillingPage() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Billing data state
  const [subscription, setSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState(null);

  // UI state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showTextUpgradeModal, setShowTextUpgradeModal] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);

  // Plan definitions with enhanced details.
  // Message counts here MUST match plan_limits.sms_limit (set by the set_plan_limits
  // DB trigger) or the card contradicts the usage meter rendered right below it.
  // Message allotments count inbound + outbound. No tier is unlimited: Scale is a
  // hard 40,000 cap that freezes outreach when reached.
  //
  // `text` is the STOCK tier figure and is what the upgrade/downgrade modals render,
  // because there the number describes a plan the tenant would switch TO. On the
  // current-plan card the same string was wrong for every custom-pricing tenant, so
  // that card resolves `limitKey` against the tenant's real plan_limits instead (see
  // resolveFeatureText). Keep both: the modals must never quote one tenant's custom
  // allotment as another tier's entitlement.
  const PLANS = {
    starter: {
      name: 'Starter',
      price: PLAN_PRICE.starter,
      priceId: 'price_1ULDXr2Mug1fO97sWvsUyufo',
      description: 'Perfect for small teams getting started',
      features: [
        { icon: MessageSquare, text: '1,000 messages per month', limitKey: 'sms_limit', label: 'messages per month', unlimitedText: 'Unlimited messages' },
        { icon: UserPlus, text: '500 new leads per month', limitKey: 'leads_limit', label: 'new leads per month', unlimitedText: 'Unlimited new leads' },
        { icon: BarChart3, text: '3 Active Campaigns', limitKey: 'campaigns_limit', label: 'Active Campaigns', labelSingular: 'Active Campaign', unlimitedText: 'Unlimited Campaigns' },
        { icon: Users, text: '1 Team Member', limitKey: 'users_limit', label: 'Team Members', labelSingular: 'Team Member', unlimitedText: 'Unlimited Team Members' },
        { icon: Shield, text: 'Basic Support' }
      ],
      level: 1,
      gradient: 'from-blue-500 to-cyan-500'
    },
    growth: {
      name: 'Growth',
      price: PLAN_PRICE.growth,
      priceId: 'price_1TDcSA2Mug1fO97sywh4ejnB',
      description: 'For growing businesses that need more',
      features: [
        { icon: MessageSquare, text: '10,000 messages per month', limitKey: 'sms_limit', label: 'messages per month', unlimitedText: 'Unlimited messages' },
        { icon: BarChart3, text: '10 Active Campaigns', limitKey: 'campaigns_limit', label: 'Active Campaigns', labelSingular: 'Active Campaign', unlimitedText: 'Unlimited Campaigns' },
        { icon: Users, text: '5 Team Members', limitKey: 'users_limit', label: 'Team Members', labelSingular: 'Team Member', unlimitedText: 'Unlimited Team Members' },
        { icon: Shield, text: 'Priority Support' }
      ],
      level: 2,
      popular: true,
      gradient: 'from-purple-500 to-pink-500'
    },
    growth_plus: {
      name: 'Growth Plus',
      price: PLAN_PRICE.growth_plus,
      priceId: 'price_1Tqya92Mug1fO97sivQSdhhy',
      description: 'Everything in Scale, sized for 25,000 messages a month',
      features: [
        { icon: MessageSquare, text: '25,000 messages per month', limitKey: 'sms_limit', label: 'messages per month', unlimitedText: 'Unlimited messages' },
        { icon: BarChart3, text: 'Unlimited Campaigns', limitKey: 'campaigns_limit', label: 'Active Campaigns', labelSingular: 'Active Campaign', unlimitedText: 'Unlimited Campaigns' },
        { icon: Users, text: '15 Team Members', limitKey: 'users_limit', label: 'Team Members', labelSingular: 'Team Member', unlimitedText: 'Unlimited Team Members' },
        { icon: Crown, text: 'White-glove onboarding & dedicated support' }
      ],
      level: 3,
      gradient: 'from-indigo-500 to-purple-500'
    },
    scale: {
      name: 'Scale',
      price: PLAN_PRICE.scale,
      priceId: 'price_1TDcSs2Mug1fO97sEHhXl38Y',
      description: 'Our highest volume tier, built for large teams',
      features: [
        { icon: MessageSquare, text: '40,000 messages per month', limitKey: 'sms_limit', label: 'messages per month', unlimitedText: 'Unlimited messages' },
        { icon: BarChart3, text: 'Unlimited Campaigns', limitKey: 'campaigns_limit', label: 'Active Campaigns', labelSingular: 'Active Campaign', unlimitedText: 'Unlimited Campaigns' },
        { icon: Users, text: '15 Team Members', limitKey: 'users_limit', label: 'Team Members', labelSingular: 'Team Member', unlimitedText: 'Unlimited Team Members' },
        { icon: Crown, text: 'Dedicated Support' }
      ],
      level: 4,
      gradient: 'from-orange-500 to-red-500'
    }
  };

  // Get API base URL
  const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    return process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://api.getsurfox.com';
  };

  // CSRF token cache
  const csrfTokenRef = React.useRef({ token: null, expiry: null });

  // Fetch CSRF token
  const getCsrfToken = async (session, forceRefresh = false) => {
    // Return cached token if still valid (with 5 min buffer)
    if (!forceRefresh && csrfTokenRef.current.token && csrfTokenRef.current.expiry && Date.now() < csrfTokenRef.current.expiry) {
      return csrfTokenRef.current.token;
    }
    if (forceRefresh) { csrfTokenRef.current.token = null; csrfTokenRef.current.expiry = null; }

    try {
      const apiBase = getApiBaseUrl();
      // CSRF endpoint is at /api/csrf-token from server root
      const csrfUrl = apiBase.includes('/api')
        ? `${apiBase}/csrf-token`
        : `${apiBase}/api/csrf-token`;

      const response = await fetch(csrfUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch CSRF token');
      }

      const data = await response.json();
      csrfTokenRef.current.token = data.csrfToken;
      // Set expiry to 5 minutes before actual expiry for safety
      csrfTokenRef.current.expiry = Date.now() + ((data.expiresIn - 300) * 1000);

      return csrfTokenRef.current.token;
    } catch (error) {
      console.error('Error fetching CSRF token:', error);
      return null;
    }
  };

  // Enhanced API call helper
  const makeAuthenticatedRequest = async (endpoint, options = {}) => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        throw new Error('Authentication required');
      }

      const method = (options.method || 'GET').toUpperCase();
      const isStateChanging = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

      const buildHeaders = async (forceRefresh = false) => {
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          ...options.headers
        };
        if (isStateChanging) {
          const csrf = await getCsrfToken(session, forceRefresh);
          if (csrf) headers['X-CSRF-Token'] = csrf;
        }
        return headers;
      };

      let response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
        ...options,
        headers: await buildHeaders(false)
      });

      // Retry once with a fresh CSRF token on stale-token 403s
      if (isStateChanging && response.status === 403) {
        let body;
        try { body = await response.clone().json(); } catch (_) {}
        if (body?.error === 'Invalid CSRF token' || body?.error === 'CSRF token missing') {
          response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
            ...options,
            headers: await buildHeaders(true)
          });
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      const responseText = await response.text();
      return responseText ? JSON.parse(responseText) : {};
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  };

  // Load billing data
  useEffect(() => {
    const loadBillingData = async () => {
      if (authLoading || !user || !user.tenant_id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      
      try {
        const [subscriptionData, paymentMethodsData, invoicesData, usageData] = await Promise.all([
          makeAuthenticatedRequest(`/billing/subscription`),
          makeAuthenticatedRequest(`/billing/payment-methods?tenant_id=${user.tenant_id}`),
          makeAuthenticatedRequest(`/billing/invoices?tenant_id=${user.tenant_id}`),
          makeAuthenticatedRequest(`/billing/usage?tenant_id=${user.tenant_id}`)
        ]);

        setSubscription(subscriptionData);
        setPaymentMethods(paymentMethodsData || []);
        setInvoices(invoicesData || []);
        setUsage(usageData);
        
      } catch (err) {
        console.error("Failed to load billing data", err);
        setError(`Failed to load billing data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadBillingData();
  }, [user?.tenant_id, authLoading]);

  // Action handlers
  const handleUpgrade = async (newPlan) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await makeAuthenticatedRequest('/billing/upgrade-subscription', {
        method: 'POST',
        body: JSON.stringify({ new_plan: newPlan })
      });

      setSuccess(`Successfully upgraded to ${PLANS[newPlan].name}! You've been charged a prorated amount.`);
      setShowUpgradeModal(false);
      
      const subscriptionData = await makeAuthenticatedRequest(`/billing/subscription`);
      setSubscription(subscriptionData);
      
    } catch (err) {
      setError(`Upgrade failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDowngrade = async (newPlan) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await makeAuthenticatedRequest('/billing/downgrade-subscription', {
        method: 'POST',
        body: JSON.stringify({ new_plan: newPlan })
      });

      setSuccess(`Downgrade to ${PLANS[newPlan].name} applied. No refund for remaining days — access continues to period end.`);
      setShowDowngradeModal(false);
      
      const subscriptionData = await makeAuthenticatedRequest(`/billing/subscription`);
      setSubscription(subscriptionData);
      
    } catch (err) {
      setError(`Downgrade failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setSaving(true);
      setError('');
      
      await makeAuthenticatedRequest('/billing/cancel-subscription', {
        method: 'POST',
        body: JSON.stringify({})
      });
      
      setSuccess('Subscription cancelled. Access retained until period end.');
      setShowCancelModal(false);
      
      const subscriptionData = await makeAuthenticatedRequest(`/billing/subscription`);
      setSubscription(subscriptionData);
      
    } catch (err) {
      setError(`Cancellation failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setSaving(true);
      setError('');
      
      await makeAuthenticatedRequest('/billing/reactivate-subscription', {
        method: 'POST',
        body: JSON.stringify({})
      });
      
      setSuccess('Subscription reactivated successfully!');
      
      const subscriptionData = await makeAuthenticatedRequest(`/billing/subscription`);
      setSubscription(subscriptionData);
      
    } catch (err) {
      setError(`Reactivation failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentMethodSuccess = async (message) => {
    setSuccess(message);
    setShowPaymentMethodModal(false);
    const paymentMethodsData = await makeAuthenticatedRequest(`/billing/payment-methods?tenant_id=${user.tenant_id}`);
    setPaymentMethods(paymentMethodsData || []);
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    try {
      setSaving(true);
      setError('');
      
      await makeAuthenticatedRequest(`/billing/payment-methods/${paymentMethodId}`, {
        method: 'DELETE',
        body: JSON.stringify({})
      });
      
      setSuccess('Payment method removed');
      const paymentMethodsData = await makeAuthenticatedRequest(`/billing/payment-methods?tenant_id=${user.tenant_id}`);
      setPaymentMethods(paymentMethodsData || []);
      
    } catch (err) {
      setError(`Failed to remove payment method: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  // Get current plan details
  const getCurrentPlanDetails = () => {
    // No subscription data at all
    if (!subscription) return null;
    
    // Subscription exists but no plan field
    if (!subscription.plan) return null;
    
    // Status is explicitly 'none' or 'canceled' - don't show plan
    if (subscription.status === 'none' || subscription.status === 'canceled') return null;
    
    // Valid states: 'active', 'trialing', 'past_due' - show the plan
    return PLANS[subscription.plan];
  };

  const currentPlanDetails = getCurrentPlanDetails();

  // The current-plan card describes THIS tenant's entitlement, so its numbers come
  // from the same plan_limits the usage meter below it reads, not from the tier's
  // stock copy. Custom-pricing tenants (tenants.is_custom_pricing) carry their own
  // plan_limits: The Da Silva Team is on `scale` with sms_limit 50,000, and the
  // hardcoded Scale bullet told them 40,000 while the meter one card down said
  // 50,000. HealthPlus is `scale` with campaigns_limit 100 against a card claiming
  // Unlimited.
  //
  // Falls back to the stock string whenever /billing/usage did not load or the key
  // is absent from plan_limits, so a failed fetch degrades to today's copy rather
  // than a blank or a zero. Only the card uses this; the upgrade/downgrade modals
  // still render feature.text, because their numbers belong to other tiers.
  //
  // Deliberately excludes topup_messages_current_cycle: this line is the recurring
  // monthly allotment, while the meter's total adds one-time top-ups for the
  // current cycle. A tenant mid-topup will see the meter total exceed this figure,
  // which is correct.
  const resolveFeatureText = (feature) => {
    if (!feature.limitKey || !usage) return feature.text;
    const value = usage[feature.limitKey];
    if (value === null || value === undefined) return feature.unlimitedText || feature.text;
    const count = Number(value);
    if (!Number.isFinite(count)) return feature.text;
    const label = count === 1 && feature.labelSingular ? feature.labelSingular : feature.label;
    return `${count.toLocaleString()} ${label}`;
  };

  // Determine available actions based on subscription state
  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';
  const canUpgrade = hasActiveSubscription && currentPlanDetails?.level < 4;
  const canDowngrade = subscription?.status === 'active' && currentPlanDetails?.level > 1;
  const canCancel = hasActiveSubscription && !subscription?.cancelAtPeriodEnd;
  const canReactivate = subscription?.status === 'active' && subscription?.cancelAtPeriodEnd;
  const isTrialing = subscription?.status === 'trialing';
  const isPastDue = subscription?.status === 'past_due';

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading your billing information...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">Please log in to manage billing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-3">
            Billing & Subscription
          </h1>
          <p className="text-lg text-gray-600">Manage your plan, payments, and usage</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-sm animate-fade-in">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">{error}</p>
              </div>
              <button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-lg p-4 shadow-sm animate-fade-in">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="ml-3 text-green-400 hover:text-green-600">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Trial Warning */}
        {isTrialing && !subscription?.hasPaymentMethod && (
          <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 rounded-lg p-5 shadow-md animate-fade-in">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5 mr-4 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-yellow-900 mb-1">Trial Ending Soon</h3>
                <p className="text-sm text-yellow-800 mb-3">
                  Add a payment method to continue after {subscription?.trialEnd && formatDate(subscription.trialEnd)}
                </p>
                <Button size="sm" onClick={() => setShowPaymentMethodModal(true)} className="bg-yellow-600 hover:bg-yellow-700">
                  <Lock className="w-4 h-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Past Due Warning */}
        {isPastDue && (
          <div className="mb-6 bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 rounded-lg p-5 shadow-md animate-fade-in">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-4 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">Payment Failed</h3>
                <p className="text-sm text-red-800 mb-3">
                  Update your payment method immediately to restore access
                </p>
                <Button size="sm" onClick={() => setShowPaymentMethodModal(true)} className="bg-red-600 hover:bg-red-700">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Update Payment Method
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-8">
            {/* Current Plan - Hero Card */}
            {currentPlanDetails ? (
              <div className="relative overflow-hidden bg-white rounded-2xl shadow-xl border border-gray-100">
                {/* Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${currentPlanDetails.gradient} opacity-5`}></div>
                
                {/* Content */}
                <div className="relative">
                  <div className="p-8 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h2 className="text-3xl font-bold text-gray-900">{currentPlanDetails.name}</h2>
                          {subscription?.cancelAtPeriodEnd && (
                            <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-semibold rounded-full">
                              Cancelling
                            </span>
                          )}
                          {isTrialing && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                              Trial
                            </span>
                          )}
                          {subscription?.status === 'active' && !subscription?.cancelAtPeriodEnd && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full flex items-center">
                              <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600">{currentPlanDetails.description}</p>
                      </div>
                      {currentPlanDetails.popular && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-semibold shadow-lg">
                          <Crown className="w-4 h-4" />
                          Popular
                        </div>
                      )}
                    </div>

                    {/*
                      Price shown is what Stripe ACTUALLY bills this tenant, from
                      /billing/subscription -> resolveBilledPrice(). This card used to
                      quote PLAN_PRICE[plan], which is the plan's list price and was
                      wrong for every tenant on a negotiated deal — $500/mo out on The
                      Da Silva Team, who pay $1,997 against a $2,497 list under coupon
                      "DaSilva_Coupon" until 2027-01-23.

                      The list price is struck through beside it when the two differ, so
                      the discount is visible rather than silently baked in, and the
                      end date is stated because this particular coupon expires.

                      Falls back to the tier price only when Stripe could not be read.
                    */}
                    {(() => {
                      const billed = subscription?.billedPrice;
                      const list = subscription?.listPrice;
                      const isDiscounted = billed != null && list != null && billed !== list;
                      return (
                        <>
                          <div className="flex items-baseline gap-2 mb-2">
                            <span className="text-5xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                              {formatCurrency(billed != null ? billed : currentPlanDetails.price)}
                            </span>
                            <span className="text-xl text-gray-500">/month</span>
                            {isDiscounted && (
                              <span className="text-xl text-gray-400 line-through">{formatCurrency(list)}</span>
                            )}
                          </div>
                          {isDiscounted && (
                            <p className="text-sm text-emerald-700 mb-8">
                              Includes your {formatCurrency(list - billed)}/month discount
                              {subscription?.discountEndsAt
                                ? ` through ${new Date(subscription.discountEndsAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                                : ''}.
                            </p>
                          )}
                          {!isDiscounted && <div className="mb-6" />}
                        </>
                      );
                    })()}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      {currentPlanDetails.features.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                          <div key={index} className="flex items-center gap-3 text-gray-700">
                            <div className={`p-2 rounded-lg bg-gradient-to-br ${currentPlanDetails.gradient} bg-opacity-10`}>
                              <Icon className="w-5 h-5 text-gray-700" />
                            </div>
                            <span className="font-medium">{resolveFeatureText(feature)}</span>
                          </div>
                        );
                      })}
                    </div>

                    {subscription?.currentPeriodEnd && (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-5 h-5" />
                            <span className="font-medium">
                              {subscription?.cancelAtPeriodEnd ? 'Access ends' : 'Renews on'}
                            </span>
                          </div>
                          <span className="text-lg font-bold text-gray-900">
                            {formatDate(subscription.currentPeriodEnd)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="p-8 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {canUpgrade && (
                        <Button onClick={() => setShowUpgradeModal(true)} className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
                          <TrendingUp className="w-4 h-4 mr-2" />
                          Upgrade Plan
                        </Button>
                      )}
                      {canDowngrade && (
                        <Button onClick={() => setShowDowngradeModal(true)} variant="outline" className="border-2">
                          <TrendingDown className="w-4 h-4 mr-2" />
                          Downgrade
                        </Button>
                      )}
                      {canReactivate && (
                        <Button onClick={handleReactivateSubscription} disabled={saving} className="bg-gradient-to-r from-green-600 to-green-700">
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Reactivate
                        </Button>
                      )}
                      {canCancel && (
                        <Button onClick={() => setShowCancelModal(true)} variant="outline" className="border-2 border-red-200 text-red-600 hover:bg-red-50">
                          Cancel Plan
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Active Subscription</h3>
                  <p className="text-gray-600 mb-6">Choose a plan to get started with SurFox AI</p>
                  <Button size="lg" onClick={() => setShowPlansModal(true)} className="bg-gradient-to-r from-blue-600 to-blue-700">
                    <ArrowRight className="w-5 h-5 mr-2" />
                    View Plans
                  </Button>
                </div>
              </div>
            )}

            {/* Usage Card */}
            {usage && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-white rounded-xl shadow-sm">
                        <BarChart3 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Usage This Month</h2>
                        <p className="text-sm text-gray-600">Track your consumption</p>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => setShowTextUpgradeModal(true)} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-md">
                      <Zap className="w-4 h-4 mr-2" />
                      Buy More SMS
                    </Button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <PremiumUsageCard
                      icon={MessageSquare}
                      label="SMS Messages"
                      current={usage.sms_count || 0}
                      limit={usage.sms_limit}
                      topup={usage.topup_messages_current_cycle}
                      breakdown={usage.sms_breakdown}
                      color="blue"
                    />
                    <PremiumUsageCard
                      icon={BarChart3}
                      label="Active Campaigns"
                      current={usage.campaigns_count || 0}
                      limit={usage.campaigns_limit}
                      color="purple"
                    />
                    <PremiumUsageCard
                      icon={Users}
                      label="Team Members"
                      current={usage.users_count || 1}
                      limit={usage.users_limit}
                      color="green"
                    />
                    {usage.voice_minutes_limit !== null && (
                      <PremiumUsageCard
                        icon={Phone}
                        label="Voice AI Minutes"
                        current={Math.round(usage.voice_minutes_used || 0)}
                        limit={usage.voice_minutes_limit}
                        color="orange"
                        footer={usage.voice_overage_minutes > 0 && (
                          <p className="text-xs font-semibold text-orange-600 mt-1">
                            +{usage.voice_overage_minutes.toLocaleString()} min over — est. ${usage.voice_overage_cost_estimate.toFixed(2)} this cycle
                          </p>
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Methods */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-white rounded-xl shadow-sm">
                      <CreditCard className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Payment Methods</h2>
                      <p className="text-sm text-gray-600">Manage your cards</p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => setShowPaymentMethodModal(true)} className="bg-gradient-to-r from-indigo-600 to-indigo-700 shadow-md">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Card
                  </Button>
                </div>
              </div>

              <div className="p-6">
                {paymentMethods.length > 0 ? (
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <div key={method.id} className="group relative flex items-center justify-between p-5 border-2 border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-14 h-10 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-md">
                              <CreditCard className="w-7 h-7 text-white" />
                            </div>
                            {method.is_default && (
                              <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                <CheckCircle className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-lg">
                              {method.brand?.toUpperCase()} •••• {method.last4}
                            </div>
                            <div className="text-sm text-gray-500">
                              Expires {method.exp_month}/{method.exp_year}
                            </div>
                          </div>
                          {method.is_default && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full">
                              DEFAULT
                            </span>
                          )}
                        </div>
                        
                        <button
                          onClick={() => handleRemovePaymentMethod(method.id)}
                          disabled={saving}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-5 h-5 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CreditCard className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-600 font-medium">No payment methods added</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-8">
           {/* Billing History */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden sticky top-8">
              <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white rounded-xl shadow-sm">
                    <Calendar className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Billing History</h2>
                    <p className="text-sm text-gray-600">Invoices & purchases</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {invoices.length > 0 ? (
                  <div className="space-y-3">
                    {invoices.slice(0, 10).map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <div className="flex-1 min-w-0">
                          {/* Date */}
                          <div className="text-sm font-semibold text-gray-900 mb-1">
                            {formatDate(item.created)}
                          </div>
                          
                          {/* Description with icon */}
                          <div className="text-xs text-gray-600 mb-1 flex items-center gap-1.5">
                            {item.type === 'text_upgrade' ? (
                              <>
                                <Zap className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                                <span className="truncate">{item.description}</span>
                              </>
                            ) : (
                              <>
                                <Calendar className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                                <span className="truncate">{item.number || item.description}</span>
                              </>
                            )}
                          </div>
                          
                          {/* Status Badge */}
                          <div className={`inline-block mt-1 px-2 py-0.5 text-xs font-bold rounded ${
                            item.status === 'paid' ? 'bg-green-100 text-green-700' : 
                            item.status === 'open' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.status === 'paid' ? 'PAID' : 
                             item.status === 'open' ? 'PENDING' : 
                             'FAILED'}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 ml-4">
                          {/* Amount */}
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              {formatCurrency(item.amount_paid / 100)}
                            </div>
                            {item.type === 'text_upgrade' && item.upgrade_messages && (
                              <div className="text-xs text-orange-600 font-medium">
                                +{parseInt(item.upgrade_messages).toLocaleString()} SMS
                              </div>
                            )}
                          </div>
                          
                          {/* Download button (only for invoices with PDFs) */}
                          {item.invoice_pdf && (
                            <a
                              href={item.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-white rounded-lg transition-colors"
                              title="Download Invoice PDF"
                            >
                              <Download className="w-5 h-5 text-blue-600" />
                            </a>
                          )}
                          
                          {/* Placeholder for text upgrades (no PDF) */}
                          {item.type === 'text_upgrade' && (
                            <div className="w-9" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Calendar className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600">No billing history yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showPlansModal && (
          <PlansModal
            currentPlan={subscription?.plan}
            onClose={() => setShowPlansModal(false)}
          />
        )}

        {showUpgradeModal && (
          <PremiumUpgradeModal
            currentPlan={subscription?.plan}
            plans={PLANS}
            onUpgrade={handleUpgrade}
            onClose={() => setShowUpgradeModal(false)}
            saving={saving}
          />
        )}

        {showDowngradeModal && (
          <PremiumDowngradeModal
            currentPlan={subscription?.plan}
            plans={PLANS}
            onDowngrade={handleDowngrade}
            onClose={() => setShowDowngradeModal(false)}
            saving={saving}
          />
        )}

        {showCancelModal && (
          <PremiumCancelModal
            onCancel={handleCancelSubscription}
            onClose={() => setShowCancelModal(false)}
            saving={saving}
          />
        )}

        {showPaymentMethodModal && (
          <Elements stripe={stripePromise}>
            <PremiumPaymentMethodModal
              onSuccess={handlePaymentMethodSuccess}
              onError={(msg) => setError(msg)}
              onClose={() => setShowPaymentMethodModal(false)}
              makeAuthenticatedRequest={makeAuthenticatedRequest}
              user={user}
            />
          </Elements>
        )}

        {showTextUpgradeModal && (
          <Elements stripe={stripePromise}>
            <PremiumTextUpgradeModal
              onSuccess={(msg) => {
                setSuccess(msg);
                setShowTextUpgradeModal(false);
                makeAuthenticatedRequest(`/billing/usage?tenant_id=${user.tenant_id}`).then(setUsage);
              }}
              onError={(msg) => setError(msg)}
              onClose={() => setShowTextUpgradeModal(false)}
              makeAuthenticatedRequest={makeAuthenticatedRequest}
              user={user}
              savedPaymentMethods={paymentMethods}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}

// Premium Usage Card Component
// `breakdown` is optional and only the SMS tile passes it, so the campaigns and
// team tiles render byte-identically to before. It arrives as null whenever the
// server could not stand behind the split (no billing anchor, a failed count, or
// a counter that has drifted away from the rows it claims to describe), and a
// null breakdown falls through to exactly the original single-segment bar.
function PremiumUsageCard({ icon: Icon, label, current, limit, topup = 0, color, breakdown = null, footer = null }) {
  const total = limit ? limit + topup : Infinity;
  const percentage = limit ? Math.min((current / total) * 100, 100) : 0;
  const isUnlimited = !limit || limit === null;

  const colorClasses = {
    blue: { bg: 'from-blue-500 to-blue-600', text: 'text-blue-600', light: 'bg-blue-50' },
    purple: { bg: 'from-purple-500 to-purple-600', text: 'text-purple-600', light: 'bg-purple-50' },
    green: { bg: 'from-green-500 to-green-600', text: 'text-green-600', light: 'bg-green-50' },
    orange: { bg: 'from-orange-500 to-red-500', text: 'text-orange-600', light: 'bg-orange-50' }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  // Shares are taken against the tenant's own message total, not against the plan
  // limit, so the three segments always fill the bar's used portion exactly. The
  // remainder is computed by subtraction rather than measured so rounding can
  // never leave a hairline gap between the last segment and the track.
  const splitTotal = breakdown && breakdown.total > 0 ? breakdown.total : 0;
  const deliveredShare = splitTotal ? (breakdown.delivered / splitTotal) * 100 : 0;
  const undeliverableShare = splitTotal ? (breakdown.undeliverable / splitTotal) * 100 : 0;
  const remainderShare = splitTotal ? Math.max(100 - deliveredShare - undeliverableShare, 0) : 0;
  const hasSplit = splitTotal > 0;

  // Diagonal hatch on the undeliverable segment. The existing bar already turns
  // yellow past 70% and red past 90% to warn about the plan ceiling, and that
  // warning is worth keeping, so undeliverable cannot rely on hue alone to stay
  // legible next to it. The same swatch style repeats in the legend below.
  const hatchedAmber = {
    backgroundColor: '#f59e0b',
    backgroundImage:
      'repeating-linear-gradient(45deg, rgba(255,255,255,0.45) 0, rgba(255,255,255,0.45) 2px, transparent 2px, transparent 4px)',
  };

  const deliveredBarClass =
    percentage > 90 ? 'bg-red-500' : percentage > 70 ? 'bg-yellow-500' : `bg-gradient-to-r ${colors.bg}`;

  // Zero buckets are dropped rather than printed, so a clean month reads
  // "8,849 delivered" instead of trailing a row of "0 undeliverable · 0 pending".
  const breakdownClauses = hasSplit
    ? [
        [breakdown.delivered, 'delivered'],
        [breakdown.undeliverable, 'undeliverable'],
        [breakdown.received, 'received'],
        [breakdown.pending, 'pending'],
      ]
        .filter(([n]) => n > 0)
        .map(([n, word]) => `${n.toLocaleString()} ${word}`)
    : [];

  return (
    <div className={`p-5 rounded-xl ${colors.light} border border-gray-200`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 rounded-lg bg-gradient-to-br ${colors.bg} shadow-md`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <span className="font-semibold text-gray-900">{label}</span>
      </div>
      
      <div className="mb-2">
        <div className="flex justify-between items-baseline">
          <span className="text-3xl font-bold text-gray-900">{current.toLocaleString()}</span>
          <span className="text-sm text-gray-600">
            {!isUnlimited ? `/ ${total.toLocaleString()}` : '/ ∞'}
          </span>
        </div>
      </div>
      
      {!isUnlimited && (
        <>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2 overflow-hidden">
            {hasSplit ? (
              // Same track, same height, same used-width. Only the fill is
              // subdivided, so the meter still reads against the plan limit
              // exactly as it did before.
              <div className="flex h-2.5 transition-all" style={{ width: `${percentage}%` }}>
                {breakdown.delivered > 0 && (
                  <div className={`h-2.5 ${deliveredBarClass}`} style={{ width: `${deliveredShare}%` }} />
                )}
                {breakdown.undeliverable > 0 && (
                  <div className="h-2.5" style={{ ...hatchedAmber, width: `${undeliverableShare}%` }} />
                )}
                {remainderShare > 0 && (
                  <div className="h-2.5 bg-gray-400" style={{ width: `${remainderShare}%` }} />
                )}
              </div>
            ) : (
              <div
                className={`h-2.5 rounded-full transition-all ${
                  percentage > 90 ? 'bg-red-500' : percentage > 70 ? 'bg-yellow-500' : `bg-gradient-to-r ${colors.bg}`
                }`}
                style={{ width: `${percentage}%` }}
              />
            )}
          </div>

          {hasSplit && (
            <>
              {/* Labelled swatches, so the segments are never distinguished by
                  colour alone. Each swatch repeats its segment's exact fill. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-1.5">
                {breakdown.delivered > 0 && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${deliveredBarClass}`} />
                    Delivered
                  </span>
                )}
                {breakdown.undeliverable > 0 && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={hatchedAmber} />
                    Undeliverable
                  </span>
                )}
                {remainderShare > 0 && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0 bg-gray-400" />
                    Received / pending
                  </span>
                )}
              </div>

              <p className="text-xs font-medium text-gray-700">
                {breakdownClauses.join(' · ')}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Usage includes all messages sent, including those undeliverable or blocked by carriers.
              </p>
            </>
          )}

          {topup > 0 && (
            <p className={`text-xs font-semibold ${colors.text} mt-1`}>
              +{topup.toLocaleString()} topup purchased
            </p>
          )}

          {footer}
        </>
      )}
    </div>
  );
}

// Plans Modal — the full catalog, for tenants with no active subscription.
//
// This is a COMPARISON view, not a checkout. It deliberately does not start a
// Stripe session: the public /subscribe flow is built for brand-new signups (its
// checkout.session.completed handler creates a tenant, and bails out early when
// the email already has a users_profile), so running an existing tenant through
// it charges the card without restoring their subscription. Reactivation goes
// through support until that path is fixed.
function PlansModal({ currentPlan, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-7xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 p-8 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">SurFox AI Plans</h3>
              <p className="text-gray-600">
                Pay for messages, not headcount. Every plan includes the same AI that texts, qualifies, and books your leads.
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-lg transition-colors flex-shrink-0">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {PLAN_CATALOG.map((plan) => {
            const isCurrent = plan.slug === currentPlan;
            return (
              <div
                key={plan.slug}
                className={`relative flex flex-col border-2 rounded-2xl p-6 transition-all ${
                  plan.popular
                    ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                    <span className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg">
                      <Crown className="w-3.5 h-3.5" />
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-2xl font-bold text-gray-900">{plan.name}</h4>
                    {isCurrent && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">
                        CURRENT
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{plan.desc}</p>
                </div>

                <div className="mb-1 flex items-baseline gap-1.5">
                  <span className="text-4xl font-bold text-gray-900">
                    ${plan.price.toLocaleString()}
                  </span>
                  <span className="text-gray-600">/ mo</span>
                </div>
                <div className="text-sm font-semibold text-gray-700 mb-4">{plan.volume}</div>

                <div className="text-sm text-gray-700 bg-white/70 border border-gray-200 rounded-lg p-3 mb-6">
                  {plan.callout}
                </div>

                <div className="space-y-5 flex-1">
                  {plan.groups.map((group) => (
                    <div key={group.h}>
                      <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                        {group.h}
                      </div>
                      <ul className="space-y-2">
                        {group.items.map((item) => (
                          <li key={item.t} className="flex items-start gap-2 text-sm">
                            {item.off ? (
                              <X className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            ) : (
                              <Check className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                            )}
                            <span className={item.off ? 'text-gray-400 line-through' : 'text-gray-700'}>
                              {item.t}
                              {item.sub && (
                                <span className="block text-xs text-gray-500 mt-0.5 no-underline">
                                  {item.sub}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-8 pt-0">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
            <p className="text-gray-900 font-semibold mb-1">Ready to start, or want to restart your plan?</p>
            <p className="text-sm text-gray-600 mb-5">
              Email us and we will get your subscription active on the plan you pick, usually the same day.
              Every plan is backed by a 30-day money-back guarantee. No setup fees, no contracts.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="mailto:support@getsurfox.com?subject=Activate%20my%20SurFox%20plan">
                <Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700">
                  Contact Support
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="https://getsurfox.com/pricing" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full sm:w-auto border-2">
                  Full Pricing Page
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Premium Upgrade Modal
function PremiumUpgradeModal({ currentPlan, plans, onUpgrade, onClose, saving }) {
  const currentLevel = plans[currentPlan]?.level || 0;
  const availablePlans = Object.entries(plans).filter(([key, plan]) => plan.level > currentLevel);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-8 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Upgrade Your Plan</h3>
              <p className="text-gray-600">Unlock more features with instant upgrade • Prorated billing</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-lg transition-colors">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {availablePlans.map(([key, plan]) => {
            const Icon = plan.features[0].icon;
            return (
              <div key={key} className={`relative border-2 rounded-2xl p-6 hover:shadow-xl transition-all ${plan.popular ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50' : 'border-gray-200 hover:border-gray-300'}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-bold rounded-full shadow-lg">
                      <Crown className="w-4 h-4" />
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-600">/month</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => {
                    const FeatureIcon = feature.icon;
                    return (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <FeatureIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature.text}</span>
                      </li>
                    );
                  })}
                </ul>
                
                <Button 
                  onClick={() => onUpgrade(key)} 
                  disabled={saving}
                  className={`w-full ${plan.popular ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' : 'bg-gray-900 hover:bg-gray-800'}`}
                >
                  {saving ? 'Processing...' : `Upgrade to ${plan.name}`}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            );
          })}
        </div>

        <div className="p-6 bg-blue-50 border-t border-blue-100">
          <p className="text-sm text-blue-900 text-center">
            <strong>💳 Instant Upgrade:</strong> You'll be charged a prorated amount immediately
          </p>
        </div>
      </div>
    </div>
  );
}

// Premium Downgrade Modal
function PremiumDowngradeModal({ currentPlan, plans, onDowngrade, onClose, saving }) {
  const currentLevel = plans[currentPlan]?.level || 999;
  const availablePlans = Object.entries(plans).filter(([key, plan]) => plan.level < currentLevel);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-8 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-yellow-50">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Downgrade Your Plan</h3>
              <p className="text-gray-600">Plan switches immediately • No refund for remaining time</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-lg transition-colors">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {availablePlans.map(([key, plan]) => (
            <div key={key} className="border-2 border-gray-200 rounded-2xl p-6 hover:border-gray-300 hover:shadow-xl transition-all">
              <h4 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h4>
              <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                  <span className="text-gray-600">/month</span>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <Icon className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature.text}</span>
                    </li>
                  );
                })}
              </ul>
              
              <Button 
                onClick={() => onDowngrade(key)} 
                disabled={saving}
                variant="outline"
                className="w-full border-2"
              >
                {saving ? 'Processing...' : `Downgrade to ${plan.name}`}
              </Button>
            </div>
          ))}
        </div>

        <div className="p-6 bg-yellow-50 border-t border-yellow-100">
          <p className="text-sm text-yellow-900 text-center">
            <strong>Immediate Switch:</strong> Plan changes now — no refund for remaining days on current plan
          </p>
        </div>
      </div>
    </div>
  );
}

// Premium Cancel Modal
function PremiumCancelModal({ onCancel, onClose, saving }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
        <div className="p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Cancel Subscription?</h3>
          <p className="text-gray-600 mb-8">
            You'll retain access until the end of your billing period, but you'll lose access to all features after that.
          </p>
          
          <div className="flex gap-3">
            <Button onClick={onClose} variant="outline" className="flex-1 border-2">
              Keep Plan
            </Button>
            <Button 
              onClick={onCancel} 
              disabled={saving}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {saving ? 'Cancelling...' : 'Yes, Cancel'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Premium Payment Method Modal
function PremiumPaymentMethodModal({ onSuccess, onError, onClose, makeAuthenticatedRequest, user }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initSetup = async () => {
      try {
        const response = await makeAuthenticatedRequest('/billing/create-setup-intent', {
          method: 'POST',
          body: JSON.stringify({})
        });
        setClientSecret(response.client_secret);
      } catch (err) {
        onError(`Failed to initialize: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    initSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setProcessing(true);
    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) }
      });

      if (error) {
        onError(error.message);
      } else {
        await makeAuthenticatedRequest('/billing/update-payment-method', {
          method: 'POST',
          body: JSON.stringify({ payment_method_id: setupIntent.payment_method })
        });
        onSuccess('Payment method added successfully!');
      }
    } catch (err) {
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-3xl p-12 max-w-md w-full mx-4 shadow-2xl">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Initializing secure payment...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
        <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Add Payment Method</h3>
              <p className="text-sm text-gray-600">Securely add your card details</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-lg transition-colors">
              <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">Card Information</label>
            <div className="border-2 border-gray-200 rounded-xl p-4 focus-within:border-blue-500 transition-colors">
              <CardElement options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#1f2937',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    '::placeholder': { color: '#9ca3af' },
                    iconColor: '#6b7280'
                  },
                  invalid: { color: '#ef4444', iconColor: '#ef4444' }
                }
              }} />
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <Lock className="w-4 h-4" />
              <span>Secured by Stripe • Your data is encrypted</span>
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1 border-2">
              Cancel
            </Button>
            <Button type="submit" disabled={!stripe || processing} className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700">
              {processing ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </span>
              ) : (
                'Add Card'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Premium Text Upgrade Modal
function PremiumTextUpgradeModal({ onSuccess, onError, onClose, makeAuthenticatedRequest, user, savedPaymentMethods }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const defaultMethod = savedPaymentMethods?.find(m => m.is_default) || savedPaymentMethods?.[0] || null;
  const [useNewCard, setUseNewCard] = useState(!defaultMethod);
  // Fallback only - the real tiers come from /billing/text-upgrade-tiers below.
  // Keep in sync with src/lib/topupTiers.server.js so a fetch failure doesn't quote
  // a stale price the checkout won't honour (the server always charges from its own
  // table, so a mismatch here misleads rather than undercharges).
  const [tiers, setTiers] = useState([
    { tier: '1k', messages: 1000, price: 85, perK: 85 },
    { tier: '5k', messages: 5000, price: 400, perK: 80 },
    { tier: '10k', messages: 10000, price: 750, perK: 75, popular: true },
  ]);

  useEffect(() => {
    makeAuthenticatedRequest('/billing/text-upgrade-tiers')
      .then(data => {
        const formatted = Object.entries(data)
          .map(([key, val]) => ({
            tier: key,
            messages: val.messages,
            price: val.price / 100,
            // val.price is CENTS. $/1k = (cents / 100) / (messages / 1000) = cents * 10 / messages.
            // The old form divided cents by messages, which is cents-PER-MESSAGE, and then
            // rendered it as "$X/1k" - understating every tier by exactly 10x ($6/1k shown
            // for a $60/1k product). The hardcoded defaults below were right, so this only
            // ever surfaced when the tiers fetch succeeded.
            perK: Math.round((val.price * 10 / val.messages) * 10) / 10,
            popular: key === '10k'
          }))
          .sort((a, b) => a.messages - b.messages);
        setTiers(formatted);
      })
      .catch(() => {}); // keep hardcoded defaults on error
  }, []);

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!stripe || !selectedTier) return;
    if (useNewCard && !elements) return;

    setProcessing(true);
    try {
      const response = await makeAuthenticatedRequest('/billing/purchase-text-upgrade', {
        method: 'POST',
        body: JSON.stringify({ upgrade_tier: selectedTier.tier })
      });

      const paymentMethod = useNewCard
        ? { card: elements.getElement(CardElement) }
        : defaultMethod.id;

      const { error } = await stripe.confirmCardPayment(response.client_secret, {
        payment_method: paymentMethod
      });

      if (error) {
        onError(error.message);
      } else {
        onSuccess(`Successfully purchased ${selectedTier.messages.toLocaleString()} messages!`);
      }
    } catch (err) {
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  if (showPaymentForm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
        <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden">
          <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-red-50">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Complete Purchase</h3>
            <p className="text-sm text-gray-600">Secure payment for SMS upgrade</p>
          </div>

          <form onSubmit={handlePurchase} className="p-8 space-y-6">
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6 text-center">
              <div className="text-sm text-gray-600 mb-2">One-time purchase</div>
              <div className="text-4xl font-bold text-gray-900 mb-2">
                {selectedTier.messages.toLocaleString()}
              </div>
              <div className="text-gray-600 mb-3">SMS Messages</div>
              <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                ${selectedTier.price}
              </div>
            </div>

            {defaultMethod && !useNewCard ? (
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">Payment Method</label>
                <div className="flex items-center justify-between border-2 border-orange-400 rounded-xl p-4 bg-orange-50">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-orange-500" />
                    <span className="text-sm font-medium text-gray-800 capitalize">
                      {defaultMethod.brand} ending in {defaultMethod.last4}
                    </span>
                  </div>
                  <button type="button" onClick={() => setUseNewCard(true)} className="text-xs text-orange-600 hover:text-orange-800 font-medium underline">
                    Use different card
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-900">Card Information</label>
                  {defaultMethod && (
                    <button type="button" onClick={() => setUseNewCard(false)} className="text-xs text-orange-600 hover:text-orange-800 font-medium underline">
                      Use saved card
                    </button>
                  )}
                </div>
                <div className="border-2 border-gray-200 rounded-xl p-4 focus-within:border-orange-500 transition-colors">
                  <CardElement options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#1f2937',
                        '::placeholder': { color: '#9ca3af' }
                      }
                    }
                  }} />
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button type="button" onClick={() => setShowPaymentForm(false)} variant="outline" className="flex-1 border-2">
                Back
              </Button>
              <Button type="submit" disabled={!stripe || processing} className="flex-1 bg-gradient-to-r from-orange-600 to-red-600">
                {processing ? 'Processing...' : `Pay $${selectedTier.price}`}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl overflow-hidden">
        <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-red-50">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Buy More SMS Messages</h3>
              <p className="text-gray-600">One-time purchase • Instant delivery • No commitment</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-lg transition-colors">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div 
              key={tier.tier}
              onClick={() => setSelectedTier(tier)}
              className={`relative cursor-pointer border-2 rounded-2xl p-6 transition-all ${
                tier.popular ? 'border-orange-500 bg-gradient-to-br from-orange-50 to-red-50' : 'border-gray-200'
              } ${selectedTier?.tier === tier.tier ? 'ring-4 ring-orange-300 scale-105' : 'hover:border-orange-300 hover:shadow-lg'}`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-full shadow-lg">
                    <Zap className="w-3.5 h-3.5" />
                    BEST VALUE
                  </span>
                </div>
              )}
              
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {tier.messages.toLocaleString()}
                </div>
                <div className="text-gray-600 mb-6 font-medium">Messages</div>
                <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                  ${tier.price}
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  ${tier.perK}/1k messages
                </div>
              </div>
              
              {selectedTier?.tier === tier.tier && (
                <div className="mt-4 flex items-center justify-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-8 border-t border-gray-100">
          <Button 
            onClick={() => setShowPaymentForm(true)} 
            disabled={!selectedTier}
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-lg py-6"
          >
            <Zap className="w-5 h-5 mr-2" />
            Continue to Payment
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}